'use client'
import { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface CartActionButtonProps {
  isInCart: boolean
  loading: boolean
  onAdd: () => void
  onRemove: () => void
  productName?: string
  addLabel?: string
  removeLabel?: string
  addIcon?: ReactNode
  addDisabled?: boolean
  className?: string
}

const LoadingSpinner = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

const CartActionButton = ({
  isInCart,
  loading,
  onAdd,
  onRemove,
  productName,
  addLabel = 'Add to Cart',
  removeLabel = 'Remove',
  addIcon,
  addDisabled = false,
  className,
}: CartActionButtonProps) => {
  if (isInCart) {
    return (
      <Button
        title="Remove from Cart"
        size="lg"
        aria-label={`Remove ${productName || 'item'} from cart`}
        className={cn(
          'w-full text-label-md-medium bg-danger-50 hover:bg-danger-100 text-danger-600 shadow-none border border-danger-200 transition-all duration-300 active:scale-[0.97]',
          className
        )}
        onClick={onRemove}
        disabled={loading}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <LoadingSpinner />
            Removing
          </span>
        ) : (
          removeLabel
        )}
      </Button>
    )
  }

  return (
    <Button
      variant="agilus_primary"
      size="lg"
      className={cn(
        'w-full text-label-md-medium text-white-100 shadow-none active:scale-[0.97] transition-all duration-200',
        className
      )}
      onClick={onAdd}
      disabled={loading || addDisabled}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <LoadingSpinner />
          Adding
        </span>
      ) : (
        <>
          {addIcon}
          {addLabel}
        </>
      )}
    </Button>
  )
}

export default CartActionButton
